import React, { useState, useEffect } from "react";
import subscriptionState from '../../../shared/mobx/subscription-state'; // Adjust the path as necessary

interface ManagePackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  planId?: string;
  features: Features;
  onSave: (updatedFeatures: Features) => void;
}

interface Option {
  value: string;
  allowed: boolean;
  tooltip?: boolean;
  tooltip_text?: string;
  label: string;
}

interface Category {
  label: string;
  options: Record<string, Option>;
  tooltip?: boolean;
  tooltip_text?: string;
}

interface Features {
  [key: string]: Category;
}

const ManagePackageModal: React.FC<ManagePackageModalProps> = ({
  isOpen,
  onClose,
  planId,
  features,
  onSave,
}) => {
  const [updatedFeatures, setUpdatedFeatures] = useState<Features>(features);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [newOptionValue, setNewOptionValue] = useState<string>("");
  const [newOptionLabel, setNewOptionLabel] = useState<string>("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    setUpdatedFeatures(features);
  }, [features]);

  const handleInputChange = (category: string, key: string, newValue: string) => {
    setUpdatedFeatures((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        options: {
          ...prev[category].options,
          [key]: {
            ...prev[category].options[key],
            value: newValue,
          },
        },
      },
    }));
  };

  const handleCheckboxChange = (category: string, key: string, isChecked: boolean) => {
    setUpdatedFeatures((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        options: {
          ...prev[category].options,
          [key]: {
            ...prev[category].options[key],
            allowed: isChecked,
          },
        },
      },
    }));
  };

  const handleAddOption = (category: string) => {
    if (newOptionValue.trim() === "") return;
    const lowerCaseOption = newOptionValue.trim().toLowerCase();
    const optionLabel = newOptionLabel.trim() === "" ? lowerCaseOption : newOptionLabel;
    setUpdatedFeatures((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        options: {
          ...prev[category].options,
          [lowerCaseOption]: { 
            value: lowerCaseOption, 
            allowed: false, 
            label: optionLabel
          },
        },
      },
    }));
    setNewOptionValue("");
    setNewOptionLabel("");
    setActiveCategory(null);
  };

  const handleSubmit = () => setShowConfirmation(true);

  const confirmSave = async () => {
    try {
      if (planId) { // Ensure planId is defined
        await subscriptionState.updateSubscriptionFeatures(planId, updatedFeatures);
        // await updateTech();
        onSave(updatedFeatures);
        onClose();
      } else {
        console.error("planId is undefined");
      }
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };

  const cancelSave = () => setShowConfirmation(false);

  // const updateTech = async () => {
  //   const requestBody = { access: updatedFeatures };
  //   try {
  //     await axios.put(
  //       `http://localhost:5000/subscription-description-list?plan_id=${planId}`,
  //       requestBody
  //     );
  //     console.log("Update successful");
  //   } catch (error) {
  //     console.error("Error updating Features:", error);
  //   }
  // };

  const handleTooltipChange = (category: string, key: string, newTooltipText: string) => {
    setUpdatedFeatures((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        options: {
          ...prev[category].options,
          [key]: {
            ...prev[category].options[key],
            tooltip_text: newTooltipText,
          },
        },
      },
    }));
  };

  const handleTooltipCheckboxChange = (category: string, key: string, isChecked: boolean) => {
    setUpdatedFeatures((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        options: {
          ...prev[category].options,
          [key]: {
            ...prev[category].options[key],
            tooltip: isChecked,
          },
        },
      },
    }));
  };

  const handleLabelChange = (category: string, newLabel: string) => {
    setUpdatedFeatures((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        label: newLabel,
      },
    }));
  };

  const handleLabelTooltipChange = (category: string, isChecked: boolean) => {
    setUpdatedFeatures((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        tooltip: isChecked,
      },
    }));
  };

  const handleLabelTooltipTextChange = (category: string, newTooltipText: string) => {
    setUpdatedFeatures((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        tooltip_text: newTooltipText,
      },
    }));
  };

  const handleLabelChangeForOption = (category: string, key: string, newLabel: string) => {
    setUpdatedFeatures((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        options: {
          ...prev[category].options,
          [key]: {
            ...prev[category].options[key],
            label: newLabel,
          },
        },
      },
    }));
  };

  const handleDeleteOption = (category: string, key: string) => {
    setUpdatedFeatures((prev) => {
      const { [key]: _, ...remainingOptions } = prev[category].options; // Remove the option
      return {
        ...prev,
        [category]: {
          ...prev[category],
          options: remainingOptions,
        },
      };
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal show" style={{ display: isOpen ? "block" : "none" }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title">Manage Package Features</h2>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body" style={{ overflowY: "auto", maxHeight: "70vh" }}>
            {Object.entries(updatedFeatures).map(([category, { label, options, tooltip, tooltip_text }]) => (
              <div key={category} className="mb-4">
                <div className="d-flex align-items-center mb-3">
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => handleLabelChange(category, e.target.value)}
                    className="form-control form-control-lg"
                    placeholder="Category Label"
                  />
                  <div className="d-flex align-items-center ms-3">
                    <label className="me-2">Tooltip</label>
                    <input
                      type="checkbox"
                      checked={tooltip}
                      onChange={(e) => handleLabelTooltipChange(category, e.target.checked)}
                    />
                    {tooltip && (
                      <input
                        type="text"
                        value={tooltip_text}
                        onChange={(e) => handleLabelTooltipTextChange(category, e.target.value)}
                        placeholder="Tooltip Text"
                        className="form-control ms-2"
                        style={{ width: "200px" }}
                      />
                    )}
                  </div>
                </div>
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Value</th>
                      <th>Allowed</th>
                      <th>Tooltip</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(options).map(([key, { value, allowed, tooltip, tooltip_text, label: optionLabel }]) => (
                      <tr key={key}>
                        <td>
                          <input
                            type="text"
                            value={optionLabel}
                            onChange={(e) => handleLabelChangeForOption(category, key, e.target.value)}
                            className="form-control"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => handleInputChange(category, key, e.target.value)}
                            className="form-control"
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={allowed}
                            onChange={(e) => handleCheckboxChange(category, key, e.target.checked)}
                          />
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <input
                              type="checkbox"
                              checked={tooltip}
                              onChange={(e) => handleTooltipCheckboxChange(category, key, e.target.checked)}
                            />
                            {tooltip && (
                              <input
                                type="text"
                                value={tooltip_text}
                                onChange={(e) => handleTooltipChange(category, key, e.target.value)}
                                placeholder="Tooltip Text"
                                className="form-control ms-2"
                                style={{ width: "200px" }}
                              />
                            )}
                            <button onClick={() => handleDeleteOption(category, key)} className="btn btn-secondary">
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {activeCategory === category && (
                      <tr>
                        <td colSpan={4}>
                          <div className="d-flex">
                           
                            <input
                              type="text"
                              value={newOptionLabel}
                              onChange={(e) => setNewOptionLabel(e.target.value)}
                              placeholder="Add new option label"
                              className="form-control me-2"
                            />
                             <input
                              type="text"
                              value={newOptionValue}
                              onChange={(e) => setNewOptionValue(e.target.value)}
                              placeholder="Add new option value"
                              className="form-control me-2"
                            />
                            <button onClick={() => handleAddOption(category)} className="btn btn-primary">
                              Add
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <button onClick={() => setActiveCategory(category)} className="btn btn-secondary mt-2">
                  + Add Option
                </button>
              </div>
            ))}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={handleSubmit}>
              Save Changes
            </button>
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
          {showConfirmation && (
            <div className="modal-backdrop fade show">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Confirm Save</h5>
                  </div>
                  <div className="modal-body">
                    <p style={{ color: 'black' }}>Are you sure you want to save these changes?</p>
                    {/* <p style={{ color: 'black' }}>"Are you sure you want to update this feature? This action cannot be undone."</p> */}
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={confirmSave}>
                      Yes
                    </button>
                    <button className="btn btn-secondary" onClick={cancelSave}>
                      No
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagePackageModal;